import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Face, ago } from './WallOfFame';
import './WallOfFame.css';
import './WallDrawer.css';

const API = process.env.REACT_APP_API_BASE_URL;

// same keys as REACTIONS in backend/api/Awards/Awards.js
const EMOJIS = [
  ['clap', '👏'],
  ['party', '🎉'],
  ['heart', '❤️'],
  ['fire', '🔥'],
  ['star', '⭐'],
];

const DAYS = 30; // the wall shows the last 30 days only
const PAGE = 12; // posts shown at first, and added each time the list is scrolled to the bottom

const seenKey = (role) => `rmwall_seen_${role}`;
const readSeen = (role) => {
  try {
    return Number(localStorage.getItem(seenKey(role))) || 0;
  } catch {
    return 0;
  }
};
const writeSeen = (role) => {
  try {
    localStorage.setItem(seenKey(role), String(Date.now()));
  } catch {
    /* private mode: no dot memory, that's fine */
  }
};

const EMOJI_OF = Object.fromEntries(EMOJIS);
const ROLE_LABEL = { student: 'Student', teacher: 'Teacher', admin: 'Admin' };

/* Admin only: "Who reacted" under a post, loads the names when opened */
const Reactors = ({ id, total, loadReactors }) => {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState(null);
  const [failed, setFailed] = useState(false);

  // reload when the count changes (someone reacted while the list is open)
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setFailed(false);
    loadReactors(id)
      .then((list) => alive && setPeople(list))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [open, id, total, loadReactors]);

  return (
    <div className="rmwd-who">
      <button type="button" className="rmwd-who-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide' : `Who reacted (${total})`}
      </button>
      {open && (
        failed ? (
          <p className="rmwd-who-empty">Couldn't load the names. Try again.</p>
        ) : !people ? (
          <p className="rmwd-who-empty">Loading…</p>
        ) : people.length === 0 ? (
          <p className="rmwd-who-empty">No reactions yet.</p>
        ) : (
          <ul className="rmwd-who-list">
            {people.map((p, i) => (
              <li key={`${p.role}-${p.name}-${p.emoji}-${i}`}>
                <span className="rmwd-who-emoji" aria-hidden="true">{EMOJI_OF[p.emoji]}</span>
                <span className="rmwd-who-name">{p.name}</span>
                <small>{[ROLE_LABEL[p.role], ago(p.at)].filter(Boolean).join(' · ')}</small>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
};

/* One post: who + what + emoji reactions */
const Post = ({ x, onReact, readOnly, loadReactors }) => (
  <li className={`rmwd-post ${x.mine ? 'is-mine' : ''}`}>
    <div className="rmwd-post-top">
      <Face x={x} />
      <div className="rmwf-text">
        {x.kind === 'joined' ? (
          <p className="rmwf-who">
            <b>{x.name}</b> joined the academy
            {x.teacher ? <> with <strong>{x.mine ? 'you' : `Teacher ${x.teacher}`}</strong></> : ''} 🚀
          </p>
        ) : x.kind === 'certificate' ? (
          <p className="rmwf-who">
            <b>{x.mine ? 'You' : x.name}</b> completed <strong>{x.course}</strong> 🎓
          </p>
        ) : (
          <p className="rmwf-who">
            <b>{x.mine ? 'You' : x.name}</b> earned <strong>{x.title}</strong>
          </p>
        )}
        {x.note && <p className="rmwf-note">“{x.note}”</p>}
        <small>
          {[
            x.kind === 'joined' ? (x.mine ? 'Congrats! 🎉' : 'Congrats 👏') : x.kind === 'certificate' ? 'Certificate earned' : x.period,
            x.nominatedBy ? `by Teacher ${x.nominatedBy}` : '',
            ago(x.at),
          ]
            .filter(Boolean)
            .join(' · ')}
        </small>
      </div>
    </div>
    {!readOnly && (
    <div className="rmwd-reacts" role="group" aria-label="React">
      {EMOJIS.map(([key, emoji]) => {
        const r = x.reactions?.[key];
        return (
          <button
            key={key}
            type="button"
            className={`rmwd-react ${r?.mine ? 'is-on' : ''} ${r?.n ? 'has-n' : ''}`}
            aria-pressed={!!r?.mine}
            aria-label={`${emoji} ${r?.n || 0}`}
            onClick={() => onReact(x.id, key)}
          >
            <span aria-hidden="true">{emoji}</span>
            {r?.n ? <b>{r.n}</b> : null}
          </button>
        );
      })}
    </div>
    )}
    {loadReactors && (() => {
      const total = Object.values(x.reactions || {}).reduce((s, r) => s + (r?.n || 0), 0);
      return total > 0 ? <Reactors id={x.id} total={total} loadReactors={loadReactors} /> : null;
    })()}
  </li>
);

/* The wall in a drawer: a button on the right edge of the page opens it.
   Everyone sees new students ("joined the academy with Teacher …"), finished courses ("completed … 🎓")
   and the wall of fame, in one list, newest first.
   role: 'teacher' | 'student' | 'admin' (with reactions), 'demo' (free-trial pages, no login, no reactions).
   Rendered inside the layout (.rmtl / .rmsl) so it follows the light / dark theme. */
const WallDrawer = ({ role = 'teacher', token }) => {
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState(null);
  const [seen, setSeen] = useState(() => readSeen(role));
  const [count, setCount] = useState(PAGE);
  const endRef = useRef(null);
  const bodyRef = useRef(null);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const demo = role === 'demo';
  // admin: /api/admin/awards/wall/… (same routes as teachers and students)
  const base = role === 'admin' ? `${API}/api/admin/awards/wall` : `${API}/api/${role}/awards`;
  const headers = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(() => {
    const get = (url, opts) => axios.get(url, opts).then((r) => r.data?.feed || []).catch(() => []);
    let calls;
    if (demo) {
      calls = [
        get(`${API}/api/public/showcase`),
        get(`${API}/api/public/showcase/joined`),
        get(`${API}/api/public/showcase/certificates`),
      ];
    } else {
      if (!token) return Promise.resolve();
      calls = [get(`${base}/feed`, headers), get(`${base}/joined`, headers), get(`${base}/certificates`, headers)];
    }
    return Promise.all(calls).then((lists) => {
      const since = Date.now() - DAYS * 86400000;
      const all = lists
        .flat()
        .filter((x) => new Date(x.at).getTime() >= since)
        .sort((a, b) => new Date(b.at) - new Date(a.at));
      setFeed(all);
    });
  }, [base, headers, role, token, demo]);

  // load once on the page (for the "new" dot), again each time the drawer opens
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    writeSeen(role);
    setCount(PAGE);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => panelRef.current?.focus(), 50);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open, load, role]);

  const close = () => {
    setOpen(false);
    setSeen(Date.now());
    btnRef.current?.focus();
  };

  const fresh = (feed || []).filter((x) => new Date(x.at).getTime() > seen).length;

  const react = (id, key) => {
    // show it right away, then use what the server says
    setFeed((list) =>
      (list || []).map((x) => {
        if (x.id !== id) return x;
        const cur = x.reactions?.[key] || { n: 0, mine: false };
        const next = { n: Math.max(0, cur.n + (cur.mine ? -1 : 1)), mine: !cur.mine };
        return { ...x, reactions: { ...x.reactions, [key]: next } };
      })
    );
    axios
      .post(`${base}/react`, { item: id, emoji: key }, headers)
      .then((r) => setFeed((list) => (list || []).map((x) => (x.id === id ? { ...x, reactions: r.data?.reactions || {} } : x))))
      .catch(() => load());
  };

  // admin only: names of who reacted to a post
  const loadReactors = useCallback(
    (id) => axios.get(`${base}/reactors`, { ...headers, params: { item: id } }).then((r) => r.data?.people || []),
    [base, headers]
  );

  const all = feed || [];
  const shown = all.slice(0, count);
  const more = all.length > count;

  // reached the bottom of the list: show the next 12
  useEffect(() => {
    const el = endRef.current;
    if (!open || !more || !el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setCount((n) => n + PAGE),
      { root: bodyRef.current, rootMargin: '0px 0px 120px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [open, more, count]);

  if (!token && !demo) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`rmwd-tab ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(true)}
        aria-label={fresh ? `Academy wall, ${fresh} new` : 'Academy wall'}
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={faTrophy} />
        <span className="rmwd-tab-label">Wall</span>
        {fresh > 0 && <i className="rmwd-dot">{fresh > 9 ? '9+' : fresh}</i>}
      </button>

      <div className={`rmwd ${open ? 'is-open' : ''}`} aria-hidden={!open}>
        <button type="button" className="rmwd-scrim" tabIndex={-1} aria-label="Close" onClick={close} />
        <aside
          ref={panelRef}
          className="rmwd-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Academy wall"
          tabIndex={-1}
        >
          <header className="rmwd-head">
            <h2>
              <span aria-hidden="true">🏆</span> Academy wall
            </h2>
            <button type="button" className="rmwd-close" onClick={close} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </header>

          <div className="rmwd-body" ref={bodyRef}>
            {!feed ? (
              <p className="rmwd-empty">Loading…</p>
            ) : shown.length === 0 ? (
              <p className="rmwd-empty">
                Nothing on the wall yet. New students and badges will show up here! 🚀
              </p>
            ) : (
              <ul className="rmwd-list">
                {shown.map((x) => (
                  <Post key={x.id} x={x} onReact={react} readOnly={demo} loadReactors={role === 'admin' ? loadReactors : null} />
                ))}
              </ul>
            )}
            {more ? (
              <div ref={endRef} className="rmwd-more">
                <button type="button" onClick={() => setCount((n) => n + PAGE)}>
                  Show more
                </button>
              </div>
            ) : (
              shown.length > 0 && <p className="rmwd-end">That's everything from the last {DAYS} days ✨</p>
            )}
            {demo && <p className="rmwd-cta">Enroll to earn badges, win challenges and see your name on the wall! 🌟</p>}
          </div>
        </aside>
      </div>
    </>
  );
};

export default WallDrawer;
